import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { 
  Home,
  Sparkles, 
  // Heart, 
  Users,
  Users2,
  BarChart3,
  Crown,
  User,
  MessageCircle,
  Bot,
  Mic,
  MoreHorizontal,
  ChevronUp,
  Coins
} from 'lucide-react'

const MobileBottomNav = () => {
  const location = useLocation()
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  const mainNavItems = [
    { path: '/home', label: 'Home', icon: Home },
    { path: '/personal-chat', label: 'Astrology', icon: Sparkles },
    { path: '/group-compatibility', label: 'Relationships', icon: Users2 },
    { path: '/matchmaking', label: 'Matchmaking', icon: Users },
  ]

  const moreNavItems = [
    { path: '/personal-chat', label: 'Personal Chat', icon: Bot },
    { path: '/live-personal-chat', label: 'Voice Chat', icon: Mic },
    { path: '/group-compatibility-chat', label: 'Group Chat', icon: MessageCircle },
    { path: '/user-journey', label: 'Analytics', icon: BarChart3 },
    { path: '/subscription', label: 'Plans', icon: Crown },
    { path: '/rraasi-coins', label: 'Coins', icon: Coins },
    { path: '/profile', label: 'Profile', icon: User },
  ]

  const toggleMoreMenu = () => {
    setShowMoreMenu(!showMoreMenu)
  }

  return (
    <>
      {/* More Menu Overlay */}
      {showMoreMenu && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setShowMoreMenu(false)}
        />
      )}

      {/* More Menu Dropdown */}
      {showMoreMenu && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="lg:hidden fixed bottom-20 left-4 right-4 z-50 bg-slate-800/95 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl"
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">More Options</h3>
              <button
                onClick={toggleMoreMenu}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <ChevronUp className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {moreNavItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowMoreMenu(false)}
                    className="flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-200 hover:bg-white/10"
                  >
                    <div className={`relative p-3 rounded-xl transition-all duration-200 ${
                      isActive 
                        ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20' 
                        : 'bg-white/5'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        isActive ? 'text-purple-400' : 'text-white'
                      }`} />
                    </div>
                    <span className={`text-xs font-medium mt-2 text-center ${
                      isActive ? 'text-purple-400' : 'text-white'
                    }`}>
                      {item.label}
                    </span>
                  </Link>
                )
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Bottom Navigation */}
      <motion.nav
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-slate-900/95 to-slate-900/90 backdrop-blur-lg border-t border-white/10"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around px-2 py-3">
          {mainNavItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 rounded-xl transition-all duration-200"
              >
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  className={`relative flex flex-col items-center space-y-1 ${
                    isActive ? 'text-purple-400' : 'text-gray-400'
                  }`}
                >
                  <div className={`relative p-2 rounded-xl transition-all duration-200 ${
                    isActive 
                      ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20' 
                      : 'hover:bg-white/5'
                  }`}>
                    <Icon className="w-5 h-5" />
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl"
                        initial={false}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </div>
                  <span className={`text-xs font-medium truncate max-w-full ${
                    isActive ? 'text-purple-400' : 'text-gray-400'
                  }`}>
                    {item.label}
                  </span>
                </motion.div>
              </Link>
            )
          })}

          {/* More Button */}
          <button
            onClick={toggleMoreMenu}
            className="flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 rounded-xl transition-all duration-200"
          >
            <motion.div
              whileTap={{ scale: 0.9 }}
              className={`relative flex flex-col items-center space-y-1 ${
                showMoreMenu ? 'text-purple-400' : 'text-gray-400'
              }`}
            >
              <div className={`relative p-2 rounded-xl transition-all duration-200 ${
                showMoreMenu 
                  ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20' 
                  : 'hover:bg-white/5'
              }`}>
                <MoreHorizontal className="w-5 h-5" />
                {showMoreMenu && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </div>
              <span className={`text-xs font-medium truncate max-w-full ${
                showMoreMenu ? 'text-purple-400' : 'text-gray-400'
              }`}>
                More
              </span>
            </motion.div>
          </button>
        </div>
      </motion.nav>
    </>
  )
}

export default MobileBottomNav 